import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Player } from '../../types/Player';
import { formatDateString } from '../../lib/utils';
import JerseyBadge from './JerseyBadge';

interface DuplicateJerseyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (playerId: string) => void;
  players: Player[];
  jerseyNumber: string; // The number that caused the duplicates
}

const DuplicateJerseyModal: React.FC<DuplicateJerseyModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  players,
  jerseyNumber
}) => {
  // Don't render if not open or no players to show
  if (!isOpen || players.length === 0) return null;

  return (
    // Modal backdrop
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out">
      {/* Modal content */}
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md max-h-[80vh] flex flex-col transform transition-all duration-300 ease-in-out scale-95 opacity-0 animate-modal-fade-in">
        {/* Modal Header */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold flex items-center text-orange-600">
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
            Multiple Players Found (#{jerseyNumber})
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition rounded-full p-1 -mr-2"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Explanatory Text */}
        <p className="text-sm text-gray-600 mb-4">
          The following players are checked in with jersey number <span className="font-semibold">{jerseyNumber}</span>. Use the jersey color to pick the correct player.
        </p>

        {/* Player List Area */}
        <div className="space-y-3 overflow-y-auto flex-grow pr-1 -mr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {players.map(player => (
            <button
              key={player.id}
              onClick={() => onSelect(player.id)}
              className="w-full text-left border border-gray-200 hover:border-blue-400 hover:bg-blue-50 rounded-lg p-3 transition flex items-start space-x-3 group"
            >
              <JerseyBadge
                number={player.jerseyNumber}
                color={player.jerseyColor}
                size="md"
                className="mt-0.5 flex-shrink-0"
              />
              <div className="flex-grow">
                <h3 className="font-medium text-base group-hover:text-blue-800">
                  {(player.first_name ?? '') + ' ' + (player.last_name ?? '')}
                </h3>
                {/* Player Details */}
                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                   {player.birthdate && (
                    <p>
                      DOB: {formatDateString(player.birthdate, 'en-US', 'short')}
                    </p>
                   )}
                   {(player.profile?.positions?.length ?? 0) > 0 && (
                      <p>
                          Positions: {(player.profile?.positions ?? []).join(', ')}
                      </p>
                   )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer/Actions */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            type="button"
            className="w-full bg-gray-100 hover:bg-gray-200 rounded-lg py-2.5 font-medium transition text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            Cancel Selection
          </button>
        </div>
      </div>

      {/* Basic CSS for animation (add to your global CSS or tailwind config if needed) */}
      <style>{`
        @keyframes modal-fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-modal-fade-in {
          animation: modal-fade-in 0.3s ease-out forwards;
        }
        /* Simple scrollbar styling */
        .scrollbar-thin { scrollbar-width: thin; }
        .scrollbar-thumb-gray-300::-webkit-scrollbar-thumb { background-color: #d1d5db; border-radius: 6px; border: 3px solid transparent; background-clip: content-box; }
        .scrollbar-track-gray-100::-webkit-scrollbar-track { background-color: #f3f4f6; border-radius: 6px; }
        .scrollbar-thin::-webkit-scrollbar { width: 10px; height: 10px; }
      `}</style>
    </div>
  );
};

export default DuplicateJerseyModal;